/**
 * Subcomponents for PersonalActivityReport — split out to keep the main
 * component lean. Same dark theme as MasterAuditEnhanced.
 */

import React from 'react'
import type { PersonalActivityRow } from '../../lib/personalActivityExport'

const CATEGORIES: Array<{ value: string; label: string }> = [
  { value: '', label: 'All categories' },
  { value: 'navigation', label: 'Navigation' },
  { value: 'search', label: 'Searches' },
  { value: 'kyc', label: 'KYC' },
  { value: 'payment', label: 'Payments' },
  { value: 'commission', label: 'Commissions' },
  { value: 'withdrawal', label: 'Withdrawals' },
  { value: 'user_edit', label: 'User edits' },
  { value: 'export', label: 'Exports' },
  { value: 'system', label: 'System' },
  { value: 'other', label: 'Other' }
]

const CAT_COLORS: Record<string, string> = {
  navigation: '#60A5FA',
  search: '#F59E0B',
  kyc: '#10B981',
  payment: '#86EFAC',
  commission: '#A78BFA',
  withdrawal: '#FCA5A5',
  user_edit: '#F472B6',
  export: '#FBBF24',
  system: '#9CA3AF',
  other: '#D1D5DB'
}

const btn = (bg: string, color = '#fff'): React.CSSProperties => ({
  padding: '8px 14px', backgroundColor: bg, color, border: 'none',
  borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: 600
})

export function PersonalActivityHeader(props: {
  collapsed: boolean; onToggle: () => void;
  rowCount: number; total: number; loading: boolean;
  onRefresh: () => void;
  onExportCsv: () => void; onExportXls: () => void; onExportPdf: () => void;
}) {
  const { collapsed, onToggle, rowCount, total, loading, onRefresh, onExportCsv, onExportXls, onExportPdf } = props
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <button onClick={onToggle} aria-expanded={!collapsed} style={{ ...btn('transparent'), padding: '4px 8px' }}>
          {collapsed ? '▸' : '▾'}
        </button>
        <div>
          <h2 style={{ margin: 0, color: '#FFFFFF', fontSize: '18px', fontWeight: 700 }}>
            🕓 My Activity (last 7 days)
          </h2>
          <p style={{ margin: '4px 0 0 0', color: '#9CA3AF', fontSize: '12px' }}>
            Personal-view only · pruned automatically · never reads from financial audit ledgers
          </p>
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
        <span style={{ color: '#9CA3AF', fontSize: '12px' }}>
          Showing <strong style={{ color: '#fff' }}>{rowCount}</strong> of <strong style={{ color: '#fff' }}>{total}</strong>
        </span>
        <button onClick={onRefresh} disabled={loading} style={btn('#374151')}>
          {loading ? 'Loading…' : '↻ Refresh'}
        </button>
        <button onClick={onExportCsv} disabled={!rowCount} style={btn('#10B981')}>CSV</button>
        <button onClick={onExportXls} disabled={!rowCount} style={btn('#3B82F6')}>XLS</button>
        <button onClick={onExportPdf} disabled={!rowCount} style={btn('#F59E0B', '#000')}>PDF</button>
      </div>
    </div>
  )
}

export function PersonalActivityBody(props: {
  from: string; to: string; category: string; q: string;
  setFrom: (v: string) => void; setTo: (v: string) => void;
  setCategory: (v: string) => void; setQ: (v: string) => void;
  rows: PersonalActivityRow[]; loading: boolean; error: string;
}) {
  const { from, to, category, q, setFrom, setTo, setCategory, setQ, rows, loading, error } = props
  return (
    <div style={{ marginTop: '16px' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'flex-end', marginBottom: '12px' }}>
        <Field label="From"><input type="date" value={from} onChange={e => setFrom(e.target.value)} style={inputStyle} /></Field>
        <Field label="To"><input type="date" value={to} onChange={e => setTo(e.target.value)} style={inputStyle} /></Field>
        <Field label="Category">
          <select value={category} onChange={e => setCategory(e.target.value)} style={inputStyle}>
            {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </Field>
        <Field label="Search">
          <input type="text" value={q} onChange={e => setQ(e.target.value)} placeholder="Action / target / query…" style={{ ...inputStyle, minWidth: '240px' }} />
        </Field>
      </div>
      {error && <div style={{ color: '#FCA5A5', fontSize: '13px', marginBottom: '8px' }}>⚠ {error}</div>}
      <div style={{ overflowX: 'auto', maxHeight: '420px', overflowY: 'auto', border: '1px solid #374151', borderRadius: '8px' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
          <thead style={{ position: 'sticky', top: 0, backgroundColor: '#111827', zIndex: 1 }}>
            <tr>
              {['Time (UTC)', 'Category', 'Action', 'Target', 'Search', 'Surface', 'Route'].map(h => (
                <th key={h} style={thStyle}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && !loading && (
              <tr><td colSpan={7} style={{ padding: '24px', textAlign: 'center', color: '#9CA3AF' }}>No activity in this window.</td></tr>
            )}
            {rows.map(r => (
              <tr key={r.id} style={{ borderTop: '1px solid #1F2937' }}>
                <td style={tdStyle}>{new Date(r.created_at).toISOString().replace('T', ' ').slice(0, 19)}</td>
                <td style={{ ...tdStyle, color: CAT_COLORS[r.event_category] || '#fff', fontWeight: 600 }}>{r.event_category}</td>
                <td style={tdStyle}>{r.event_action}</td>
                <td style={tdStyle}>
                  {r.target_label || r.target_id || ''}
                  {r.target_type ? <span style={{ color: '#6B7280' }}> · {r.target_type}</span> : null}
                </td>
                <td style={tdStyle}>{r.search_query || ''}</td>
                <td style={tdStyle}>{r.search_surface || ''}</td>
                <td style={tdStyle}>{r.tab_id || r.route_path || ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

const inputStyle: React.CSSProperties = {
  padding: '8px 10px', backgroundColor: '#111827', color: '#fff',
  border: '1px solid #374151', borderRadius: '6px', fontSize: '13px'
}
const thStyle: React.CSSProperties = {
  padding: '8px 10px', textAlign: 'left', color: '#9CA3AF',
  fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap'
}
const tdStyle: React.CSSProperties = { padding: '6px 10px', color: '#E5E7EB', verticalAlign: 'top' }

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
      <span style={{ color: '#9CA3AF', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</span>
      {children}
    </label>
  )
}
